/**
 * The campaign tags an ad click arrives with (utm_*, gclid, fbclid), carried
 * from page to page IN THE URL until signup reads them.
 *
 * Signup only reads them off its own URL (SignupClient `collectAttribution`),
 * so any hop on the way there that rebuilds the URL loses them. Two hops did:
 * the auth wall's 307 to /login cleared the whole query, and /login's "Sign
 * up" link carried only `next`. On production 37 of the 82 untagged signups in
 * the six weeks to 23 Oct 2026 arrived at /signup from another app page —
 * that was where their tags went.
 *
 * Why the URL and not a cookie: storing them would be non-essential storage
 * under UK PECR, so it could only happen with marketing consent (the same gate
 * the Meta Pixel waits on, src/lib/consent/marketing-consent.ts) — which most
 * visitors never give. A query param is not storage.
 *
 * Pure and directive-free: the edge middleware, server pages and client
 * components all import it (anti-pattern #18).
 */

/** Subset of ATTRIBUTION_KEYS (src/lib/hubspot/attribution.ts) — the URL-borne ones. */
export const CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
] as const;

const MAX_LEN = 200;
// Control characters and markup never belong in a campaign tag; a value that
// carries them is dropped rather than repaired.
const UNSAFE = /[\u0000-\u001f\u007f<>"'`]/;

interface ParamReader {
  get(name: string): string | null;
}

/** The campaign tags present on `from`, cleaned. Unknown params are never included. */
export function campaignParams(from: ParamReader): [string, string][] {
  const out: [string, string][] = [];
  for (const key of CAMPAIGN_PARAMS) {
    const value = from.get(key)?.trim();
    if (!value || value.length > MAX_LEN || UNSAFE.test(value)) continue;
    out.push([key, value]);
  }
  return out;
}

/** Copy the campaign tags (and only those) from one query onto another. */
export function copyCampaignParams(from: ParamReader, to: URLSearchParams): void {
  for (const [key, value] of campaignParams(from)) to.set(key, value);
}

/** `path` with `extra` params and any campaign tags from `from` appended. */
export function withCampaignParams(path: string, from: ParamReader, extra: Record<string, string | null | undefined> = {}): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(extra)) if (v) q.set(k, v);
  copyCampaignParams(from, q);
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}
